import { memo, useMemo } from 'react';

import CollapsibleMultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/CollapsibleMultiSelectionFilter';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import NameFilter from '@components/ui/filters/NameFilter/NameFilter';
import PriceRangeFilter from '@components/ui/filters/PriceRangeFilter/PriceRangeFilter';
import SingleSelectionFilter from '@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import {
  StyledResponsiveFilterCollapse,
  StyledResponsiveHotelNameFilter,
  StyledResponsiveHotelClassFilter,
  StyledResponsiveSingleSelectionFilterContainer,
  StyledResponsiveSingleSelectionFilterItemContainer,
} from '@components/views/components/listing/drawer/ResponsiveListingFilterAndSortStyle';
import { useFiltersContext } from '@contexts/Filters';
import ClassFilter from '@components/ui/filters/ClassFilter/ClassFilter';
import ReviewFilter from '@components/ui/filters/ReviewFilter/ReviewFilter';
import { EntityFilter } from '@components/ui/filters/FiltersBlock';
import RegionFilter from '@components/views/components/RegionFilterWithSearchBar';
import ExpediaReviewFilter from '@components/ui/filters/ExpediaReviewFilter';
import { RegionProps } from '@components/views/components/RegionFilterWithSearchBar/interface';
import MultiSelectionFilterWithSearchBar from '@components/ui/filters/MultiSelectionFilterWithSearchBar/ResponsiveCollapsibleMultiSelectionFilter';

import { MobileFilterItemsWrapper } from './styled';

interface ResponsiveListingFiltersInterface {
  dataRegions: Array<RegionProps> | any;
  onRegionCheck: (checkedKeysValue: any, e: any) => void;
  selectedRegions?: Array<any>;
  hotelName: string;
  setHotelName: (val: string) => void;
  isAvailableProperty: boolean;
  isAvailablePropertyCount: number;
  handleAvailableProperty: (val: boolean) => void;

  selectedStars: number[];
  handleStarFilter: (val: number[]) => void;

  freeCancellation: boolean;
  freeCancellationCount: number;
  handleFreeCancellation: (val: boolean) => void;

  selectedThemeItems: Array<ListingFilterItemKey>;
  dataThemes: Array<ListingFilterItemInterface>;
  handleThemesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  selectedFeatureItems: Array<ListingFilterItemKey>;
  dataFeatures: Array<ListingFilterItemInterface>;
  handleFeaturesChecked: (itemKeys: ListingFilterItemKey[]) => void;
  featureFilter: EntityFilter[] | undefined;

  selectedCategoryItems: Array<ListingFilterItemKey>;
  dataCategories: Array<ListingFilterItemInterface>;
  handleCategoriesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  selectedAccTypes: Array<ListingFilterItemKey>;
  dataAccTypes: Array<ListingFilterItemInterface>;
  handleAccTypesChecked: (itemKeys: ListingFilterItemKey[]) => void;
  handleSelectedTripAdvisor?: (item: string) => void;
  selectedTripAdvisor?: string | string[];

  selectedPopularityItems: Array<ListingFilterItemKey>;
  dataPopularities: Array<ListingFilterItemInterface>;
  handlePopularitiesChecked: (itemKeys: ListingFilterItemKey[]) => void;

  minValue: number | undefined;
  maxValue: number | undefined;
  setMinValue: (val: number | undefined) => void;
  setMaxValue: (val: number | undefined) => void;
  clearAllFilters: () => void;
  disableClearAllFilters?: boolean;
  hotelType: 'expedia' | 'normal';
  hotelFilters?: EntityFilter[] | undefined;
  isFiltersModalVisible?: boolean;

  isCity?: boolean;
}

const ResponsiveListingFilters = ({
  dataRegions,
  onRegionCheck,
  selectedRegions,
  hotelName,
  setHotelName,
  handleAvailableProperty,
  isAvailableProperty,
  isAvailablePropertyCount,

  selectedStars,
  handleStarFilter,

  freeCancellation,
  freeCancellationCount,
  handleFreeCancellation,

  selectedThemeItems,
  dataThemes,
  handleThemesChecked,

  selectedFeatureItems,
  handleFeaturesChecked,
  featureFilter,

  selectedCategoryItems,
  dataCategories,
  handleCategoriesChecked,

  selectedPopularityItems,
  dataPopularities,
  handlePopularitiesChecked,

  selectedAccTypes,
  dataAccTypes,
  handleAccTypesChecked,
  handleSelectedTripAdvisor,
  selectedTripAdvisor,

  minValue,
  maxValue,
  setMinValue,
  setMaxValue,

  clearAllFilters,
  disableClearAllFilters,
  hotelType = 'normal',
  hotelFilters,
  isFiltersModalVisible,

  isCity = false,
}: ResponsiveListingFiltersInterface) => {
  const { setFilters } = useFiltersContext();
  const pointFilter = useMemo(() => {
    return hotelFilters?.filter((item) => {
      return item?.type === 'point';
    });
  }, [hotelFilters]);

  return (
    <>
      <ClearAllFilters
        onClick={clearAllFilters}
        disabled={disableClearAllFilters}
      />
      <StyledResponsiveFilterCollapse>
        {hotelType === 'expedia' ? (
          <>
            <StyledResponsiveSingleSelectionFilterContainer>
              {freeCancellationCount && freeCancellationCount > 0 ? (
                <StyledResponsiveSingleSelectionFilterItemContainer>
                  <SingleSelectionFilter
                    title='Ücretsiz İptal'
                    subTitle={freeCancellationCount}
                    checked={freeCancellation}
                    handleCheckBoxChange={handleFreeCancellation}
                    mode='controlled'
                  />
                </StyledResponsiveSingleSelectionFilterItemContainer>
              ) : null}
            </StyledResponsiveSingleSelectionFilterContainer>
            <MobileFilterItemsWrapper>
              <StyledResponsiveHotelClassFilter>
                <ClassFilter
                  title='Otel Sınıfı'
                  handleSearch={handleStarFilter}
                  selectedStars={selectedStars}
                />
              </StyledResponsiveHotelClassFilter>

              {pointFilter?.[0]?.items?.length ? (
                <ExpediaReviewFilter
                  title='Misafir Puanı'
                  point={hotelFilters?.filter((item) => item.type === 'point')}
                  handleSelectedItems={handleSelectedTripAdvisor}
                  selectedItems={selectedTripAdvisor}
                />
              ) : null}

              <StyledResponsiveHotelNameFilter>
                <NameFilter
                  title='Otel Adı'
                  name={hotelName}
                  setName={setHotelName}
                  placeholder='Otel adı ile arayın'
                  handleSearch={(name) =>
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      hotelName: name,
                    }))
                  }
                  collapsable={false}
                />
              </StyledResponsiveHotelNameFilter>

              <PriceRangeFilter
                minValue={minValue}
                maxValue={maxValue}
                setMinValue={setMinValue}
                setMaxValue={setMaxValue}
                title='Konaklama Fiyatı'
                collapsable={false}
              />

              {dataAccTypes?.length > 0 ? (
                <CollapsibleMultiSelectionFilter
                  title='Otel Konaklama Tipi'
                  data={dataAccTypes}
                  subTitleKey='count'
                  selectedItems={selectedAccTypes}
                  handleSelectedItems={handleAccTypesChecked}
                />
              ) : null}
            </MobileFilterItemsWrapper>
          </>
        ) : null}

        {hotelType === 'normal' ? (
          <>
            <StyledResponsiveSingleSelectionFilterContainer>
              {isAvailablePropertyCount && isAvailablePropertyCount > 0 ? (
                <StyledResponsiveSingleSelectionFilterItemContainer>
                  <SingleSelectionFilter
                    className='responsive-single-selection-filter'
                    title='Sadece müsait tesisleri göster'
                    subTitle={isAvailablePropertyCount}
                    checked={isAvailableProperty}
                    handleCheckBoxChange={handleAvailableProperty}
                    mode='controlled'
                  />
                </StyledResponsiveSingleSelectionFilterItemContainer>
              ) : null}
              {freeCancellationCount && freeCancellationCount > 0 ? (
                <StyledResponsiveSingleSelectionFilterItemContainer>
                  <SingleSelectionFilter
                    title='Ücretsiz İptal'
                    subTitle={freeCancellationCount}
                    checked={freeCancellation}
                    handleCheckBoxChange={handleFreeCancellation}
                    mode='controlled'
                  />
                </StyledResponsiveSingleSelectionFilterItemContainer>
              ) : null}
            </StyledResponsiveSingleSelectionFilterContainer>

            <MobileFilterItemsWrapper>
              {dataRegions?.length > 0 && isFiltersModalVisible ? (
                <RegionFilter
                  regions={dataRegions}
                  onCheck={onRegionCheck}
                  selectedRegions={selectedRegions}
                  isCity={isCity}
                />
              ) : null}
              <StyledResponsiveHotelNameFilter>
                <NameFilter
                  title='Otel Adı'
                  name={hotelName}
                  setName={setHotelName}
                  placeholder='Otel adı ile arayın'
                  handleSearch={(name) =>
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      hotelName: name,
                    }))
                  }
                  collapsable={false}
                />
              </StyledResponsiveHotelNameFilter>

              <PriceRangeFilter
                minValue={minValue}
                maxValue={maxValue}
                setMinValue={setMinValue}
                setMaxValue={setMaxValue}
                title='Konaklama Fiyatı'
                collapsable={false}
              />
              {pointFilter?.[0]?.items?.length ? (
                <StyledResponsiveHotelNameFilter>
                  <ReviewFilter
                    title='Misafir Puanı'
                    point={hotelFilters?.filter(
                      (item) => item.type === 'point'
                    )}
                    handleSelectedItems={handleSelectedTripAdvisor}
                    selectedItems={selectedTripAdvisor}
                  />
                </StyledResponsiveHotelNameFilter>
              ) : null}

              {dataAccTypes?.length > 0 ? (
                <CollapsibleMultiSelectionFilter
                  title='Otel Konaklama Tipi'
                  data={dataAccTypes}
                  subTitleKey='count'
                  selectedItems={selectedAccTypes}
                  handleSelectedItems={handleAccTypesChecked}
                />
              ) : null}
              {dataPopularities?.length > 0 ? (
                <MultiSelectionFilterWithSearchBar
                  title='Otel Özellikleri'
                  data={dataPopularities}
                  subTitleKey='count'
                  selectedItems={selectedPopularityItems}
                  handleSelectedItems={handlePopularitiesChecked}
                />
              ) : null}

              {featureFilter?.map((item, index) =>
                item.items.length === 0 ? null : (
                  <MultiSelectionFilterWithSearchBar
                    key={index}
                    title={item.title}
                    data={item.items}
                    subTitleKey='count'
                    selectedItems={selectedFeatureItems}
                    handleSelectedItems={handleFeaturesChecked}
                  />
                )
              )}

              {dataThemes?.length > 0 ? (
                <MultiSelectionFilterWithSearchBar
                  title='Otel Temaları'
                  data={dataThemes}
                  subTitleKey='count'
                  selectedItems={selectedThemeItems}
                  handleSelectedItems={handleThemesChecked}
                />
              ) : null}
              {dataCategories?.length > 0 ? (
                <MultiSelectionFilterWithSearchBar
                  title='Otel Tipi'
                  data={dataCategories}
                  subTitleKey='count'
                  selectedItems={selectedCategoryItems}
                  handleSelectedItems={handleCategoriesChecked}
                />
              ) : null}
            </MobileFilterItemsWrapper>
          </>
        ) : null}
      </StyledResponsiveFilterCollapse>
    </>
  );
};

export default memo(ResponsiveListingFilters);
